// API Client — single point of contact with the backend
import { t } from '../i18n';
import { state } from '../store';
import { safeStorage } from '../utils/storage';

const BASE = '';  // relative paths — works locally and in production

let _token: string | null = null;
let _reinitPromise: Promise<void> | null = null;  // single-flight reinit
let _reinitAttempts = 0;
let _halted = false;  // kill switch — после true ни один запрос не пройдёт
type TerminalReason = 'none' | 'session' | 'banned' | 'rate_limit';
let _terminalReason: TerminalReason = 'none';

const _cardsCache = new Map<string, { data: CardsResponse; ts: number }>();

let _userCache: { data: AuthUser; ts: number } | null = null;
const USER_CACHE_TTL = 60_000;

export function setToken(t: string) { _token = t; }
export function syncUserCacheCoins(newAmount: number) {
  if (_userCache) _userCache.data.coins = newAmount;
}
export function getToken() { return _token; }
export function haltAllRequests(reason: Exclude<TerminalReason, 'none'> = 'session') {
  _halted = true;
  if (_terminalReason === 'none' || reason === 'banned') _terminalReason = reason;
}
export function isHalted() { return _halted; }
export function isTerminal() { return _halted || _terminalReason !== 'none'; }

function terminateAuthFlow(err: any, fallbackStatus: number): void {
  const status = err?.status ?? fallbackStatus;
  haltAllRequests(status === 429 ? 'rate_limit' : 'session');
  _token = null;
  if (status === 429 && typeof (window as any).showToast === 'function') {
    (window as any).showToast(t('error_rate_limit') || 'Too many requests — please wait a minute', 'error');
  }
  (window as any).__showSessionExpired?.();
}

// --- Response types ---

export interface AuthUser {
  tg_id: number;
  first_name: string;
  coins: number;
  is_premium: boolean;
  premium_expires_at: string | null;
  lang: string;
}

export interface AuthResponse {
  token: string;
  user: AuthUser;
}

export interface InitResponse {
  user: AuthUser;
  games: Game[];
  referral_count: number;
}

export interface Game {
  id: string;
  icon: string;
  name: string;
  screen: string;
  rules: string;
  unlock_type: 'free' | 'coin' | 'premium' | 'invite';
  unlock_cost: number;
  user_has_access: boolean;
}

export interface Level {
  id: string;
  icon: string;
  name: string;
  desc: string;
  unlock_type: 'free' | 'coin' | 'premium' | 'invite';
  unlock_cost: number;
  user_has_access: boolean;
}

export interface TodCard {
  type: 'truth' | 'dare';
  text: string;
}

export interface CardsResponse {
  cards: string[] | TodCard[];
  card_type: 'simple' | 'tod';
  total: number;
  is_mock: boolean;
  session_seed: number;
  page?: number;
  total_pages?: number;
}

export interface BulkResultItem {
  gameId: string;
  levelId: string;
  cards: string[] | Array<{ type: 'truth' | 'dare'; text: string }>;
  card_type: 'simple' | 'tod';
  total: number;
  error?: string;
}

export interface ReferralMilestone {
  count: number;
  reward: string;
  claimed: boolean;
}

export interface ReferralStats {
  total_invited: number;
  coins_earned: number;
  referral_link: string;
  milestones: ReferralMilestone[];
  task_claims?: {
    email?: boolean;
    channel_sub?: boolean;
    allow_pm?: boolean;
  };
}

// --- Core request function ---

let _connectionErrorShown = false;

function showConnectionError() {
  if (_connectionErrorShown) return;
  _connectionErrorShown = true;

  // Remove existing overlay if any
  document.getElementById('connection-error-overlay')?.remove();

  const overlay = document.createElement('div');
  overlay.id = 'connection-error-overlay';
  overlay.style.cssText = `
    position:fixed;inset:0;z-index:9999;
    background:rgba(7,6,15,0.95);backdrop-filter:blur(12px);
    display:flex;align-items:center;justify-content:center;
    padding:24px;
  `;
  overlay.innerHTML = `
    <div style="text-align:center;max-width:320px;">
      <div style="font-size:64px;margin-bottom:16px;animation:pulse 2s ease-in-out infinite;">📡</div>
      <div style="font-family:Inter,sans-serif;font-size:22px;font-weight:800;color:#fff;margin-bottom:8px;">${t('error_connection_title')}</div>
      <div style="color:#B0ADCF;font-size:14px;font-weight:600;margin-bottom:24px;line-height:1.5;">
        ${t('error_connection_sub')}
      </div>
      <button id="connection-retry-btn" style="
        width:100%;padding:16px 24px;
        background:linear-gradient(135deg,#2D6BFF,#00E5FF);
        border:none;border-radius:9999px;color:white;
        font-size:16px;font-weight:800;cursor:pointer;
        box-shadow:0 0 24px rgba(45,107,255,0.5);
      ">${t('error_connection_btn')}</button>
      <div style="color:#6B6890;font-size:12px;font-weight:600;margin-top:16px;">${t('error_connection_help')}</div>
    </div>
  `;
  document.body.appendChild(overlay);
  overlay.querySelector<HTMLButtonElement>('#connection-retry-btn')?.addEventListener('click', () => {
    overlay.remove();
    (window as any).__connectionRetry?.();
  });
}

// Global retry callback — set by the app
(window as any).__connectionRetry = () => {
  if (_halted) return;
  _connectionErrorShown = false;
  // Re-render the current screen to trigger data refetch
  if (typeof (window as any).renderApp === 'function') {
    (window as any).renderApp();
  }
};

// Auto-retry when browser comes back online
window.addEventListener('online', () => {
  if (_halted) return;
  if (_connectionErrorShown) {
    document.getElementById('connection-error-overlay')?.remove();
    _connectionErrorShown = false;
    (window as any).__reinit?.();
  }
});

async function tryRefresh(): Promise<string | null> {
  try {
    const res = await fetch('/api/auth/refresh', {
      method: 'POST',
      credentials: 'include',
    });
    if (!res.ok) return null;
    const data = await res.json() as { token?: string };
    return data.token ?? null;
  } catch {
    return null;
  }
}

async function request<T>(path: string, options: RequestInit = {}): Promise<T> {
  // ── Kill switch: после фатальной ошибки ВСЕ запросы немедленно отклоняются ──
  if (_halted) {
    const err = new Error('API halted') as any;
    err.status = 0;
    throw err;
  }

  let res: Response;

  try {
    res = await fetch(BASE + path, {
      ...options,
      headers: {
        'Content-Type': 'application/json',
        ...(_token ? { 'Authorization': `Bearer ${_token}` } : {}),
        ...options.headers,
      },
    });
  } catch (networkError) {
    // Network failure (offline, connection reset, DNS failure, etc.)
    console.error('Network error:', networkError);
    showConnectionError();
    throw new Error('Network error — connection lost');
  }

  // Dismiss connection error overlay if request succeeded
  if (_connectionErrorShown) {
    _connectionErrorShown = false;
    document.getElementById('connection-error-overlay')?.remove();
  }

  if (!res.ok) {
    // 429 — Rate-limited: never retry, dispatch global event and bail.
    if (res.status === 429) {
      const err429 = new Error('API error 429') as any;
      err429.status = 429;
      terminateAuthFlow(err429, 429);
      throw err429;
    }

    // 401 — token expired / invalid → try silent refresh, then full reauth
    if (res.status === 401 && path !== '/api/auth' && path !== '/api/auth/refresh') {
      // If a reinit is already in flight, piggyback on it instead of spawning another
      if (_reinitPromise) {
        try {
          await _reinitPromise;
          if (isHalted()) { const e = new Error('halted') as any; e.status = 0; throw e; }
          return request<T>(path, options);
        } catch (reinitErr: any) {
          if (reinitErr?.message === 'init already running') {
            const pendingErr = new Error('reinit pending') as any;
            pendingErr.status = 0;
            throw pendingErr;
          }
          terminateAuthFlow(reinitErr, 401);
          throw reinitErr;
        }
      }

      // Try silent refresh first
      const refreshed = await tryRefresh();
      if (refreshed) {
        setToken(refreshed);
        return request<T>(path, options);
      }

      // Only increment attempts when actually starting a new reinit
      _reinitAttempts++;

      if (_reinitAttempts <= 2 && typeof (window as any).__reinit === 'function') {
        _reinitPromise = (window as any).__reinit().finally(() => { _reinitPromise = null; });
        try {
          await _reinitPromise;
          if (isHalted()) { const e = new Error('halted') as any; e.status = 0; throw e; }
          _reinitAttempts = 0;
          return request<T>(path, options);
        } catch (reinitErr: any) {
          if (reinitErr?.message === 'init already running') {
            const pendingErr = new Error('reinit pending') as any;
            pendingErr.status = 0;
            throw pendingErr;
          }
          terminateAuthFlow(reinitErr, 401);
          throw reinitErr;
        }
      } else {
        // Attempts exhausted — terminal failure
        const tooManyErr = new Error('Too many reinit attempts') as any;
        tooManyErr.status = 401;
        terminateAuthFlow(tooManyErr, 401);
        throw tooManyErr;
      }
    }

    // 403 — check if banned, session expired, or access denied
    if (res.status === 403) {
      const body = await res.clone().json().catch(() => ({}));
      const errMsg: string = (body as any)?.error || (body as any)?.message || '';

      if ((body as any)?.error === 'banned') {
        haltAllRequests('banned');
        // Show fullscreen ban overlay — not a toast
        (window as any).__showBannedScreen?.(
          (body as any).reason || 'Violation of terms',
          (body as any).lang || undefined,
        );
        throw new Error('banned');
      }

      // Session / auth errors → show session expired screen
      // Exclude /api/auth itself — init() handles its own errors.
      const isSessionError =
        path !== '/api/auth' && (
          errMsg.toLowerCase().includes('jwt expired') ||
          errMsg.toLowerCase().includes('initdata is too old') ||
          errMsg.toLowerCase().includes('unauthorized') ||
          errMsg.toLowerCase().includes('token expired')
        );

      if (isSessionError) {
        // If a reinit is already in flight, piggyback on it
        if (_reinitPromise) {
          try {
            await _reinitPromise;
            if (isHalted()) { const e = new Error('halted') as any; e.status = 0; throw e; }
            return request<T>(path, options);
          } catch (reinitErr: any) {
            if (reinitErr?.message === 'init already running') {
              const pendingErr = new Error('reinit pending') as any;
              pendingErr.status = 0;
              throw pendingErr;
            }
            terminateAuthFlow(reinitErr, 403);
            throw reinitErr;
          }
        }

        _reinitAttempts++;

        if (_reinitAttempts <= 2 && typeof (window as any).__reinit === 'function') {
          _reinitPromise = (window as any).__reinit().finally(() => { _reinitPromise = null; });
          try {
            await _reinitPromise;
            if (isHalted()) { const e = new Error('halted') as any; e.status = 0; throw e; }
            _reinitAttempts = 0;
            return request<T>(path, options);
          } catch (reinitErr: any) {
            if (reinitErr?.message === 'init already running') {
              const pendingErr = new Error('reinit pending') as any;
              pendingErr.status = 0;
              throw pendingErr;
            }
            terminateAuthFlow(reinitErr, 403);
            throw reinitErr;
          }
        } else {
          const sessionErr = new Error('session_expired') as any;
          sessionErr.status = 401;
          terminateAuthFlow(sessionErr, 401);
          throw sessionErr;
        }
      }

      // Regular 403 (e.g. level/game locked)
      if (typeof (window as any).showToast === 'function') {
        (window as any).showToast(t('error_access_denied'), 'error');
      }
    }

    const err = new Error(`API error ${res.status}`) as any;
    err.status = res.status;
    try { err.body = await res.clone().json(); } catch {}
    throw err;
  }

  return res.json();
}

// --- Typed API methods ---

export const api = {
  auth: (initData: string) => {
    const userAgent = navigator.userAgent;
    const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    const screenInfo = window.screen.width + 'x' + window.screen.height;

    return request<AuthResponse>('/api/auth', {
      method: 'POST',
      body: JSON.stringify({ 
        initData,
        userAgent,
        timezone,
        screenInfo 
      }),
    });
  },

  getInit: () =>
    request<InitResponse>('/api/init'),

  getUser: async () => {
    if (_userCache && Date.now() - _userCache.ts < USER_CACHE_TTL) {
      return _userCache.data;
    }
    const data = await request<AuthUser>('/api/user');
    _userCache = { data, ts: Date.now() };
    return data;
  },

  getGames: () =>
    request<Game[]>('/api/games'),

  getLevels: (gameId: string) =>
    request<Level[]>(`/api/levels?gameId=${gameId}`),

  getCards: async (
    gameId: string,
    levelId: string,
    lang: string,
    difficulty?: string | null,
    sessionId?: string,
    page?: number,
  ): Promise<CardsResponse> => {
    const effectiveDifficulty = difficulty !== undefined ? difficulty : state.difficultyRange;
    const effectivePage = page || 1;

    const cacheKey = `${gameId}:${levelId}:${lang}:${effectiveDifficulty || ''}:${sessionId || ''}:${effectivePage}`;
    const cached = _cardsCache.get(cacheKey);
    if (cached && Date.now() - cached.ts < 60 * 60 * 1000) {
      return cached.data;
    }

    let url = `/api/cards?gameId=${encodeURIComponent(gameId)}&levelId=${encodeURIComponent(levelId)}&lang=${lang}`;
    if (effectiveDifficulty) url += `&difficulty=${encodeURIComponent(effectiveDifficulty)}`;
    if (sessionId) url += `&sid=${encodeURIComponent(sessionId)}`;
    if (effectivePage > 1) url += `&page=${effectivePage}`;

    const data = await request<CardsResponse>(url);
    const ts = Date.now();
    _cardsCache.set(cacheKey, { data, ts });
    if (_cardsCache.size > 30) {
      let oldestKey = '';
      let oldestTs = Infinity;
      for (const [k, v] of _cardsCache) {
        if (v.ts < oldestTs) {
          oldestTs = v.ts;
          oldestKey = k;
        }
      }
      if (oldestKey) _cardsCache.delete(oldestKey);
    }
    return data;
  },

  getCardsBulk: (
    items: Array<{ gameId: string; levelId: string }>,
    lang: string,
    difficulty: string | null,
    sid?: string,
  ) =>
    request<{ results: BulkResultItem[] }>('/api/cards/bulk', {
      method: 'POST',
      body: JSON.stringify({ items, lang, difficulty, sid }),
    }),

  unlock: (gameId: string, levelId: string) =>
    request<{ coins_left: number }>('/api/unlock', {
      method: 'POST',
      body: JSON.stringify({ game_id: gameId, level_id: levelId }),
    }),

  createPurchase: (type: string) =>
    request<{ invoice_link: string }>('/api/purchase/create', {
      method: 'POST',
      body: JSON.stringify({ type }),
    }),

  getReferral: () =>
    request<ReferralStats>('/api/referral'),

  setLang: (lang: string) =>
    request<{ ok: boolean; lang: string }>('/api/user/lang', {
      method: 'PATCH',
      body: JSON.stringify({ lang }),
    }),

  setEmail: (email: string) =>
    request<{ ok: boolean; coins_added?: number; reason?: string }>('/api/user/email', {
      method: 'POST',
      body: JSON.stringify({ email }),
    }),

  claimTask: (task: string) =>
    request<{ ok: boolean; coins_added?: number; reason?: string }>('/api/tasks/claim', {
      method: 'POST',
      body: JSON.stringify({ task }),
    }),

  getTasksStatus: () =>
    request<{ email_set: boolean; channel_sub: boolean; allow_pm: boolean }>('/api/tasks/status'),
};

// --- Difficulty persistence (Telegram CloudStorage / localStorage fallback) ---

export function saveDifficulty(range: string | null): void {
  try {
    const tg = (window as any).Telegram?.WebApp;
    if (tg?.CloudStorage) {
      if (range) {
        tg.CloudStorage.setItem('pg_difficulty', range);
      } else {
        tg.CloudStorage.removeItem('pg_difficulty');
      }
    } else {
      // Fallback на localStorage
      if (range) {
        safeStorage.set('pg_difficulty', range);
      } else {
        safeStorage.remove('pg_difficulty');
      }
    }
  } catch (e) {
    console.warn('Failed to save difficulty setting', e);
  }
}

function migrateDifficulty(val: string | null): string | null {
  const MAP: Record<string, string> = { '1-4': 'easy', '4-7': 'medium', '7-10': 'hard' };
  return val ? (MAP[val] ?? val) : 'easy'; // null → 'easy' (новый дефолт)
}

export function loadDifficulty(): Promise<string | null> {
  return new Promise((resolve) => {
    try {
      const tg = (window as any).Telegram?.WebApp;
      if (tg?.CloudStorage) {
        tg.CloudStorage.getItem('pg_difficulty', (err: any, val: string) => {
          if (err || !val) {
            resolve('easy');
          } else {
            const migrated = migrateDifficulty(val);
            if (migrated !== val) {
              // Save migrated value back to CloudStorage
              tg.CloudStorage.setItem('pg_difficulty', migrated ?? '');
            }
            resolve(migrated);
          }
        });
      } else {
        const raw = safeStorage.get('pg_difficulty');
        const migrated = migrateDifficulty(raw);
        if (migrated !== raw) {
          if (migrated) safeStorage.set('pg_difficulty', migrated);
          else safeStorage.remove('pg_difficulty');
        }
        resolve(migrated);
      }
    } catch (e) {
      resolve('easy');
    }
  });
}
